import React from 'react';

import { withAssetPrefix } from 'gatsby';

import { MotionPlayer } from '@af/design-system-docs-ui/motion-player';
import Code from '@atlaskit/code/code';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Grid, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	root: {
		/* Single column on mobile, two columns from 30rem up. */
		gridTemplateColumns: '1fr',
		'@media (min-width: 30rem)': {
			gridTemplateColumns: '1fr 1fr',
		},
		paddingBlockStart: token('space.400'),
		paddingBlockEnd: token('space.400'),
	},
});

/**
 * Grid of easing-curve `MotionPlayer`s with labels and descriptions.
 */
const EasingCurves = (): React.JSX.Element => {
	return (
		<Grid testId="grid-basic" rowGap="space.150" columnGap="space.150" xcss={styles.root}>
			<Stack space="space.100">
				<MotionPlayer
					source="lottie"
					lottieSrc={withAssetPrefix('/lottie/foundations/motion/bold-out.json')}
					size="small"
					hasSpacing={false}
					label="Curve diagram of easing-out bold: a steep start that decelerates into a gentle landing at the end"
				/>
				<Heading size="small">Ease-out bold</Heading>
				<Text>
					<Code>cubic-bezier(0, 0.4, 0, 1)</Code>
				</Text>
				<Text>
					Elements arrive quickly and decelerate to a stop. Best for elements like Panel or Flag
					entering the screen.
				</Text>
			</Stack>
			<Stack space="space.100">
				<MotionPlayer
					source="lottie"
					lottieSrc={withAssetPrefix('/lottie/foundations/motion/bold-in-out.json')}
					size="small"
					hasSpacing={false}
					label="Curve diagram of easing-in-out bold, accelerating gently from rest and decelerating into a controlled landing"
				/>
				<Heading size="small">Ease-in-out bold</Heading>
				<Text>
					<Code>cubic-bezier(0.4, 0, 0, 1)</Code>
				</Text>
				<Text>
					Gentle start and soft settle. Best for scaling Modals or repositioning elements.
				</Text>
			</Stack>
			<Stack space="space.100">
				<MotionPlayer
					source="lottie"
					lottieSrc={withAssetPrefix('/lottie/foundations/motion/practical-in.json')}
					size="small"
					hasSpacing={false}
					label="Curve diagram of easing-in practical curve, accelerating subtly as an element exits"
				/>
				<Heading size="small">Ease-in practical</Heading>
				<Text>
					<Code>cubic-bezier(0.6, 0, 0.8, 0.6)</Code>
				</Text>
				<Text>
					Starts slowly and accelerates away. Best for exit transitions where elements get out of
					the way.
				</Text>
			</Stack>
			<Stack space="space.100">
				<MotionPlayer
					source="lottie"
					lottieSrc={withAssetPrefix('/lottie/foundations/motion/practical-out.json')}
					size="small"
					hasSpacing={false}
					label="Curve diagram of easing-out practical curve, decelerating subtly into a resting state"
				/>
				<Heading size="small">Ease-out practical</Heading>
				<Text>
					<Code>cubic-bezier(0.4, 1, 0.6, 1)</Code>
				</Text>
				<Text>
					Subtle, everyday entrance curve. Best for elements like Popup or hover background fades.
				</Text>
			</Stack>
		</Grid>
	);
};

export default EasingCurves;
